import React, { useEffect, useMemo, useState } from "react";
import AvailabilityCalendar from "../components/AvailabilityCalendar";

import {
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
} from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

export default function EmployeePortal({ user }) {
  const [authMode, setAuthMode] = useState("login"); // login | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get("logout") === "1") {
        signOut(auth).finally(() => {
          p.delete("logout");
          const next =
            window.location.pathname +
            (p.toString() ? `?${p.toString()}` : "") +
            window.location.hash;
          window.location.replace(next);
        });
      }
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setLoading(true);
      setError("");

      if (!u) {
        setProfile(null);
        setLoading(false);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "users", u.uid));
        if (snap.exists()) {
          setProfile({ uid: u.uid, email: u.email || "", ...snap.data() });
        } else {
          setProfile(null);
        }
      } catch (e) {
        console.error("EmployeePortal profile load failed:", e?.code, e?.message);
        setProfile(null);
        setError("Profile load failed (permissions). Try logging out and back in.");
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, []);

  const effectiveUser = useMemo(() => {
    if (user) return user;
    if (profile) return profile;
    return null;
  }, [user, profile]);

  async function handleAuth(e) {
    e.preventDefault();
    setError("");

    try {
      if (authMode === "reset") {
        await sendPasswordResetEmail(auth, email);
        setError("Password reset email sent. Check inbox/spam.");
        setAuthMode("login");
        return;
      }

      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      console.error("Auth error:", err);
      setError(err?.message || "Login failed.");
    }
  }

  if (loading) {
    return <div className="text-gray-600 p-6">Loading...</div>;
  }

  // If logged in, render content only (App.js top bar provides logout/name)
  if (effectiveUser) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-6">
        <AvailabilityCalendar user={effectiveUser} />
      </div>
    );
  }

  // Login screen
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white border rounded-xl shadow-sm p-5">
        <h1 className="text-xl font-semibold mb-2">Sign in</h1>

        {error && (
          <div className="bg-red-100 text-red-700 border border-red-300 rounded p-3 mb-3 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleAuth} className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-600">Email</label>
            <input
              className="border rounded w-full p-2"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              type="email"
              required
            />
          </div>

          {authMode === "login" && (
            <div>
              <label className="text-xs font-semibold text-gray-600">Password</label>
              <input
                className="border rounded w-full p-2"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                required
              />
            </div>
          )}

          <button className="w-full bg-blue-600 text-white rounded p-2 hover:bg-blue-700">
            {authMode === "login" ? "Sign in" : "Send reset email"}
          </button>
        </form>

        <div className="mt-3 text-sm text-gray-600">
          {authMode === "login" ? (
            <button onClick={() => setAuthMode("reset")} className="text-blue-700 hover:underline">
              Forgot password?
            </button>
          ) : (
            <button onClick={() => setAuthMode("login")} className="text-blue-700 hover:underline">
              Back to login
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
